import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { Form } from "Components/Core";
import {
  SearchControl,
  TableComponent,
  PaginationControl,
} from "Components/Shared";
import {
  getAssetReviewRequests,
  patchMarkBulkPayment,
} from "Api/Pages/AssetReviewRequestsApi";
import {
  AssetReviewRequest,
  GetAssetReviewRequestsParams,
} from "Interfaces/Pages/AssetReviewRequestsInterface";
import { INITIAL_PAGE, RECORDS_PER_PAGE } from "App/constants";
import {
  getAssetReviewRequestColumns,
  STATUS_TABS,
} from "Pages/AssetReviewRequests/constants";

type Props = {
  filters?: {
    coach_id?: number;
    requestor_id?: number;
    requestor_type?: string;
  };
  originPath?: string;
};

type ReviewRequestsOriginState = {
  __origin?: {
    currentPage?: number;
    status?: GetAssetReviewRequestsParams["status"];
    isBulkMode?: boolean;
    selectedIds?: number[];
  };
};

const ReviewRequestsList: React.FC<Props> = ({ filters, originPath }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const origin = (location.state as ReviewRequestsOriginState | null)?.__origin;
  const [currentPage, setCurrentPage] = useState<number>(
    origin?.currentPage ?? INITIAL_PAGE
  );
  const [totalPages, setTotalPages] = useState<number>(INITIAL_PAGE);
  const [searchInput, setSearchInput] = useState<string>("");
  const [status, setStatus] = useState<GetAssetReviewRequestsParams["status"]>(
    origin?.status ?? "pending"
  );
  const [isBulkMode, setIsBulkMode] = useState<boolean>(
    origin?.isBulkMode ?? false
  );
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(origin?.selectedIds || [])
  );

  const { data, refetch } = useQuery(
    ["asset-review-requests", filters, currentPage, searchInput, status],
    async () =>
      getAssetReviewRequests({
        page: currentPage,
        perPage: RECORDS_PER_PAGE,
        search: searchInput,
        status,
        order_by: "created_at",
        order_type: status === "cancelled" ? "DESC" : "ASC",
        unpaid_first: status === "completed" ? true : undefined,
        coach_id: filters?.coach_id,
        requestor_id: filters?.requestor_id,
        requestor_type: filters?.requestor_type,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const tableData = useMemo<AssetReviewRequest[]>(
    () => data?.data || [],
    [data]
  );

  useEffect(() => {
    if (data?.total_count !== undefined) {
      setTotalPages(Math.ceil(data.total_count / RECORDS_PER_PAGE));
    }
  }, [data?.total_count]);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearchInput(value);
      setCurrentPage(INITIAL_PAGE);
    },
    []
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      refetch();
    },
    [refetch]
  );

  const handleStatusChange = useCallback(
    (newStatus: GetAssetReviewRequestsParams["status"]) => {
      setStatus(newStatus);
      setCurrentPage(INITIAL_PAGE);
      setIsBulkMode(false);
      setSelectedIds(new Set());
    },
    []
  );

  const toggleBulkMode = () => {
    if (!isBulkMode) {
      setSelectedIds(new Set());
    }
    setIsBulkMode(!isBulkMode);
  };

  const handleRowCheckbox = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const markPaid = (paid: boolean) => {
    if (!selectedIds.size) return;
    patchMarkBulkPayment(Array.from(selectedIds), paid).then(() => {
      queryClient.invalidateQueries(["asset-review-requests", filters]);
      setSelectedIds(new Set());
      setIsBulkMode(false);
    });
  };

  return (
    <div className="d-flex flex-column flex-1">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`btn btn-outline-secondary ${
                status === tab.value ? "active" : ""
              }`}
              onClick={() => handleStatusChange(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-2">
          {status === "completed" && !isBulkMode && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={toggleBulkMode}
            >
              Mark Payment
            </button>
          )}
          {status === "completed" && isBulkMode && (
            <>
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={() => markPaid(true)}
              >
                Mark Paid
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => markPaid(false)}
              >
                Mark Unpaid
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={toggleBulkMode}
              >
                Cancel
              </button>
            </>
          )}
          <Form className="d-flex" onSubmit={handleSubmit}>
            <SearchControl
              className="mt-0"
              placeholder="Search"
              label="Search"
              onChange={handleSearch}
            />
          </Form>
        </div>
      </div>
      <div className="flex-1">
        <div className="table-responsive">
          <TableComponent
            columns={getAssetReviewRequestColumns({
              status,
              isBulkMode: status === "completed" && isBulkMode,
              onToggleRow: handleRowCheckbox,
            })}
            tableData={
              isBulkMode
                ? tableData.map((row) => ({
                    ...row,
                    _bulk: {
                      checked: selectedIds.has(row.id),
                    },
                  }))
                : tableData
            }
            isClickAble={true}
            onRowClick={(row: AssetReviewRequest) => {
              if (filters?.coach_id) {
                navigate(
                  `/coaches/${filters.coach_id}/coach-review-requests/${row.id}`,
                  {
                    state: {
                      ...row,
                      __origin: {
                        currentPage,
                        status,
                        isBulkMode,
                        selectedIds: Array.from(selectedIds),
                        profilePath: originPath,
                      },
                    },
                  }
                );
              } else if (
                filters?.requestor_id &&
                filters?.requestor_type === "Player"
              ) {
                navigate(
                  `/players/${filters.requestor_id}/player-review-requests/${row.id}`,
                  {
                    state: {
                      ...row,
                      __origin: {
                        currentPage,
                        status,
                        isBulkMode,
                        selectedIds: Array.from(selectedIds),
                        profilePath: originPath,
                      },
                    },
                  }
                );
              } else {
                navigate(`/review-requests/${row.id}`, {
                  state: {
                    ...row,
                    __origin: {
                      currentPage,
                      status,
                      isBulkMode,
                      selectedIds: Array.from(selectedIds),
                    },
                  },
                });
              }
            }}
          />
        </div>
      </div>
      <div className="pagination-wrap d-flex justify-content-end">
        <PaginationControl
          currentPage={currentPage}
          setCurrentPage={(current: number) => setCurrentPage(current)}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};

export default ReviewRequestsList;
